import { forwardRef, useRef } from 'react';
import styles from './SearchBar.module.scss';
import SearchIcon from 'assets/search.svg';
import CloseIcon from 'assets/close.svg';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useTranslation } from 'hooks/useTranslation';

type Props = {
  searchQuery: string;
  setQuery(newQuery: string): void;
  inputId: string;
  name?: string;
};

export const SearchBar = forwardRef<HTMLInputElement, Props>(
  ({ searchQuery, setQuery, inputId, name }, ref) => {
    const t = useTranslation();
    const internalRef = useRef<HTMLInputElement>(null);

    return (
      <div className={styles.inputContainer}>
        <SearchIcon className={styles.inputIcon} />
        <VisuallyHidden asChild>
          <label htmlFor={inputId}>{t('search')}</label>
        </VisuallyHidden>
        <input
          value={searchQuery}
          type="search"
          id={inputId}
          name={name}
          className={styles.searchBar}
          placeholder={t('search')}
          autoComplete="off"
          onChange={(e) => setQuery(e.currentTarget.value)}
          ref={(el) => {
            internalRef.current = el;
            if (ref) {
              if (typeof ref === 'function') {
                ref(el);
              } else {
                (
                  ref as React.ForwardedRef<HTMLInputElement> & {
                    current?: HTMLInputElement | null;
                  }
                ).current = el;
              }
            }
          }}
        />
        {searchQuery && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={() => {
              setQuery('');
              internalRef.current?.focus();
            }}
          >
            <CloseIcon className={styles.clearIcon} />
            <VisuallyHidden>{t('clearInput')}</VisuallyHidden>
          </button>
        )}
      </div>
    );
  }
);

SearchBar.displayName = 'SearchBar';
